import {
	faClock,
	faCodeBranch,
	faCogs,
	faDatabase,
	faEnvelope,
	faGears,
	faPlay,
	faPlus,
	faRobot,
	faToggleOff,
	faToggleOn,
	faTrash,
	faWrench,
} from '@fortawesome/pro-solid-svg-icons';
import { EmailDomainService, type DomainSummary } from '@supply-mover-2-0/core';
import {
	VARIABLE_TYPE_REGISTRY,
	type VariableTypeKey,
} from '@supply-mover-2-0/shared';
import React, {
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';
import { Icon } from '../../components/base/Icon';
import { Button } from '../../components/forms/Button';
import { Input } from '../../components/forms/Input';
import { Select } from '../../components/forms/Select';
import type { VariableMappingVariable } from '../../components/forms/VariableMapping';
import { VariableMapping } from '../../components/forms/VariableMapping';
import { Modal } from '../../components/overlays/Modal';
import { Sidebar } from '../../components/overlays/Sidebar';
import { useThemeColors } from '../../providers/ThemeProvider';
import { useTranslation } from '../../providers/TranslationProvider';
import { AgentService } from '../services/agentService';
import { AutomationApiService } from '../services/automationApi';
import type {
	AutomationStep,
	StepConfig as FlowStepConfig,
} from '../types/automation';
import type { NodeInfo } from '../utils/variableTransform';
import { transformVariableToDisplay } from '../utils/variableTransform';
import { AgentStepConfig } from './AgentStepConfig';
import { ClientBulkActionStepConfig } from './ClientBulkActionStepConfig';
import { DynamicFieldValue } from './DynamicFieldValue';
import { EmailIngestStepConfig } from './EmailIngestStepConfig';
import { EmailStepConfig } from './EmailStepConfig';
import { EvaluateStepConfig } from './EvaluateStepConfig';
import { ExternalLookupStepConfig } from './ExternalLookupStepConfig';
import { PhoneNumberField } from './PhoneNumberField';
import { ProductMatchStepConfig } from './ProductMatchStepConfig';
import { SmsStepConfig } from './SmsStepConfig';
import StartNodeConfigSidebar, {
	type StartNodeConfig,
} from './StartNodeConfigSidebar';
import { StepReferenceSelector } from './StepReferenceSelector';
import { WaitForEventStepConfig } from './WaitForEventStepConfig';

interface StepConfigSidebarProps {
	visible: boolean;
	step: AutomationStep | null;
	/** Commit on explicit Save — used by the slide-over (non-inline) mode. */
	onSave?: (updates: { config: FlowStepConfig; stepName?: string }) => void;
	onCancel: () => void;
	campaignId?: number; // Made optional for compatibility
	flowId?: number;
	versionId?: number;
	nodes?: any[];
	edges?: any[];
	stepTypeSchemas?: Map<string, Record<string, any>>;
	buildStepContext?: (nodeId: string) => any;
	allSteps?: Array<AutomationStep>; // For list builder - enables step reference selection
	/** When true, render inline (no Sidebar overlay) — used by the 2.0 builder. */
	inline?: boolean;
	/**
	 * Inline (always-save) mode: called on every edit so the host commits the
	 * change live. The host keeps the pre-edit step as the Revert snapshot.
	 * The config is a union because the start step emits a `StartNodeConfig`.
	 */
	onChange?: (updates: {
		config: FlowStepConfig | StartNodeConfig;
		stepName?: string;
	}) => void;
}

/**
 * Build the working config for a step: a shallow copy of the stored config
 * with type-specific defaults filled in. Shared by the editor's initialization
 * and its Revert action so both produce an identical baseline.
 */
function buildInitialStepConfig(step: AutomationStep): FlowStepConfig {
	const initialConfig: FlowStepConfig = { ...step.config };
	if (step.stepType === 'delay') {
		if (initialConfig.delayType == null) {
			initialConfig.delayType = 'relative';
		}
		if (initialConfig.relativeUnit == null) {
			initialConfig.relativeUnit = 'minutes';
		}
		if (initialConfig.relativeValue == null) {
			initialConfig.relativeValue = 1;
		}
	} else if (step.stepType === 'agent_call') {
		if (initialConfig.waitForCompletion == null) {
			initialConfig.waitForCompletion = true;
		}
		if (initialConfig.maxWaitTimeSeconds == null) {
			initialConfig.maxWaitTimeSeconds = 0;
		}
	} else if (step.stepType === 'product_match') {
		if (initialConfig.topN == null) {
			initialConfig.topN = 5;
		}
	} else if (step.stepType === 'external_lookup') {
		if (initialConfig.lineFilter == null) {
			initialConfig.lineFilter = 'no_match';
		}
		if (initialConfig.maxLines == null) {
			initialConfig.maxLines = 50;
		}
		if (initialConfig.concurrency == null) {
			initialConfig.concurrency = 4;
		}
		if (initialConfig.rematch == null) {
			initialConfig.rematch = true;
		}
		if (initialConfig.maxSearches == null) {
			initialConfig.maxSearches = 3;
		}
		if (initialConfig.allowFetch == null) {
			initialConfig.allowFetch = false;
		}
		// An empty allowed list is the default and means "search the open web", so
		// a new step starts with an explicit empty list rather than an absent key —
		// the form and the validator then both read the same shape. `blockedDomains`
		// gets no default: absent is what "nothing excluded" looks like.
		if (initialConfig.allowedDomains == null) {
			initialConfig.allowedDomains = [];
		}
	}
	return initialConfig;
}

const StepConfigSidebar: React.FC<StepConfigSidebarProps> = ({
	visible,
	step,
	onSave,
	onCancel,
	campaignId,
	flowId,
	versionId,
	nodes,
	edges,
	stepTypeSchemas,
	buildStepContext,
	allSteps,
	inline,
	onChange,
}) => {
	const colors = useThemeColors();
	const { t } = useTranslation();
	const [config, setConfig] = useState<FlowStepConfig>({});
	const [stepName, setStepName] = useState<string>('');
	const [templates, setTemplates] = useState<any[]>([]);
	const [agents, setAgents] = useState<any[]>([]);
	const [loading, setLoading] = useState(false);
	const [stepContext, setStepContext] = useState<any>(null);
	const [nodeMap, setNodeMap] = useState<Map<string, NodeInfo>>(new Map());
	const [showAgentInputMapping, setShowAgentInputMapping] = useState(false);
	const [selectedAgentForMapping, setSelectedAgentForMapping] =
		useState<any>(null);
	const [provisionedPhones, setProvisionedPhones] = useState<
		Array<{ phoneNumber: string }>
	>([]);
	const [phonesLoading, setPhonesLoading] = useState(false);
	const [verifiedDomains, setVerifiedDomains] = useState<Array<DomainSummary>>(
		[],
	);
	const [fallbackFrom, setFallbackFrom] = useState<string>('');
	const [domainsLoading, setDomainsLoading] = useState(false);
	const [dataLoaderTypes, setDataLoaderTypes] = useState<
		Array<{
			dataType: string;
			name: string;
			description: string;
			icon: string;
			outputSchema: Record<string, any>;
		}>
	>([]);
	// Store Start node UUID extracted once from initial config (to avoid re-extracting on every render)
	const [startNodeUUID, setStartNodeUUID] = useState<string | null>(null);
	// The step identity the editor state was last initialized from — see the
	// init effect for why this guard is needed.
	const initializedStepRef = useRef<AutomationStep | null>(null);
	// When true, the live-commit watcher skips the next config/name change
	// because it was produced by (re-)initialization, not a user edit.
	const skipCommitRef = useRef(true);

	// Helper function to format total delay
	const formatTotalDelay = (
		days: number,
		hours: number,
		minutes: number,
		seconds: number,
	) => {
		const parts = [];
		if (days > 0) {
			parts.push(`${days} day${days !== 1 ? 's' : ''}`);
		}
		if (hours > 0) {
			parts.push(`${hours} hour${hours !== 1 ? 's' : ''}`);
		}
		if (minutes > 0) {
			parts.push(`${minutes} minute${minutes !== 1 ? 's' : ''}`);
		}
		if (seconds > 0) {
			parts.push(`${seconds} second${seconds !== 1 ? 's' : ''}`);
		}

		if (parts.length === 0) {
			return 'No delay';
		}
		if (parts.length === 1) {
			return parts[0];
		}

		const lastPart = parts.pop();
		return `${parts.join(', ')} and ${lastPart}`;
	};

	useEffect(() => {
		// Editor state is (re-)initialized only when a *different* step is
		// selected. In inline (always-save) mode the host recreates its
		// `steps`/`allSteps` arrays on every keystroke, re-running this effect;
		// rebuilding config/nodeMap/context here would reset the in-progress
		// form and hand child components (notably RichTextEditor) fresh
		// references on every key press. The available variables depend only
		// on the rest of the flow, so they only need refreshing when the
		// edited step changes.
		if (step != null && initializedStepRef.current !== step) {
			initializedStepRef.current = step;
			// Build node map from nodes or allSteps (UUID -> stepName for display).
			const map = new Map<string, NodeInfo>();
			if (nodes) {
				nodes.forEach((node) => {
					if (node.id && node.data?.label) {
						// Map uses node.id (which is the persistent UUID) as key
						map.set(node.id, {
							id: node.id, // This is the persistent UUID
							stepName: node.data.label,
						});
					}
				});
				setNodeMap(map);
			} else if (allSteps) {
				// Build map from allSteps (for list builder)
				allSteps.forEach((stepItem) => {
					if (stepItem.internalId != null && stepItem.stepName) {
						// Use step.internalId (UUID) as key for variable references
						map.set(stepItem.internalId, {
							id: stepItem.internalId,
							stepName: stepItem.stepName,
						});
					}
				});
				setNodeMap(map);
			}

			// Initialize config (with type-specific defaults) and name.
			setConfig(buildInitialStepConfig(step));
			setStepName(step.stepName || '');
			// The setConfig above is initialization — not a user edit, so the
			// live-commit watcher must ignore the change it produces.
			skipCommitRef.current = true;

			// Extract Start node UUID from initial config once (to keep it stable for getAvailableDataSources)
			const configStr = JSON.stringify(step.config);
			const uuidMatch = configStr.match(
				/\{\{([a-f0-9-]+)\.(Email|Name|Phone|Company|Address|City|State|Zip|Notes)\}\}/,
			);

			if (uuidMatch != null) {
				setStartNodeUUID(uuidMatch[1] ?? null);
			} else {
				setStartNodeUUID(null);
			}

			if (step.stepType === 'template' || step.stepType === 'send_email') {
				loadTemplates();
			} else if (step.stepType === 'agent' || step.stepType === 'agent_call') {
				loadAgents();
			} else if (step.stepType === 'load_data') {
				loadDataLoaderTypes();
			}

			// Build step context from flow state if available, otherwise fetch from backend
			if (buildStepContext) {
				// Use in-memory flow state
				// For list builder, step.id is used; for graph builder, step.internalId is used
				const contextKey = step.internalId ?? step.id?.toString() ?? '';
				if (contextKey) {
					const context = buildStepContext(contextKey);
					setStepContext(context);
				}
			} else if (flowId && versionId && step.id) {
				// Fallback to backend (though we're not using this anymore)
				loadStepContext(flowId, versionId, step.id);
			}
		}
	}, [step, flowId, versionId, buildStepContext, nodes, allSteps]);

	// Inline (always-save) mode: every config/name edit is pushed to the host
	// immediately, so switching to another step never loses work. The first
	// change after each (re-)initialization is the initialization itself and
	// is skipped via `skipCommitRef`.
	useEffect(() => {
		if (inline !== true || onChange == null) {
			return;
		}
		if (skipCommitRef.current) {
			skipCommitRef.current = false;
			return;
		}
		onChange({ config, stepName });
	}, [config, stepName, inline, onChange]);

	// Load agent details for agent_call steps when agentId changes
	useEffect(() => {
		if (step?.stepType === 'agent_call' && config.agentId) {
			loadAgentDetails(config.agentId);
		}
	}, [step?.stepType, config.agentId]);

	// Load provisioned phones for agent_call and send_sms steps
	useEffect(() => {
		if (
			visible &&
			(step?.stepType === 'agent_call' || step?.stepType === 'send_sms')
		) {
			setPhonesLoading(true);
			AgentService.getProvisionedPhones()
				.then((result) => {
					setProvisionedPhones(result.data);
				})
				.catch(() => {
					setProvisionedPhones([]);
				})
				.finally(() => {
					setPhonesLoading(false);
				});
		}
	}, [visible, step?.stepType]);

	// Load verified domains for send_email steps
	useEffect(() => {
		if (visible && step?.stepType === 'send_email') {
			setDomainsLoading(true);
			EmailDomainService.listDomains()
				.then((result) => {
					// Only show verified domains in the dropdown
					setVerifiedDomains(result.domains.filter((d) => d.verified));
					setFallbackFrom(result.fallbackFrom);
				})
				.catch(() => {
					setVerifiedDomains([]);
				})
				.finally(() => {
					setDomainsLoading(false);
				});
		}
	}, [visible, step?.stepType]);

	const loadAgentDetails = async (agentId: number) => {
		try {
			const result = await AgentService.getAgent(agentId);
			if (result.success && result.data) {
				setSelectedAgentForMapping(result.data);
			}
		} catch (error) {
			console.error('Failed to load agent details:', error);
		}
	};

	const loadTemplates = async () => {
		try {
			setLoading(true);
			const templatesData = await new AutomationApiService().getTemplates();
			setTemplates(templatesData);
		} catch (error) {
			// Failed to load templates
		} finally {
			setLoading(false);
		}
	};

	const loadAgents = async () => {
		try {
			setLoading(true);
			const result = await AgentService.getAgents({});

			if (result.success && result.data) {
				// Filter for active agents - handle both boolean and numeric values
				const activeAgents = result.data.filter(
					(agent) => agent.isActive === true || agent.isActive === 1,
				);
				setAgents(activeAgents);
			}
		} catch (error) {
			// Failed to load agents
		} finally {
			setLoading(false);
		}
	};

	const loadDataLoaderTypes = async () => {
		try {
			const loaderTypes = await AutomationApiService.getDataLoaderTypes();
			setDataLoaderTypes(loaderTypes);
		} catch (error) {
			console.error('Failed to load data loader types:', error);
		}
	};

	const loadStepContext = async (
		flowId: number,
		versionId: number,
		stepId: number,
	) => {
		try {
			const apiService = new AutomationApiService();
			const context = await apiService.getStepContext(
				flowId,
				versionId,
				stepId,
			);
			setStepContext(context);
		} catch (error) {
			console.error('Failed to load step context:', error);
		}
	};

	// Helper to get display text for a value (for preview/display only).
	// Accepts unknown because some step-config editors hold their config as a
	// plain record; only string values are transformed.
	const getDisplayText = (value: unknown): string => {
		if (typeof value !== 'string') {
			return value == null ? '' : String(value);
		}
		if (value === '' || nodeMap.size === 0) {
			return value;
		}
		return transformVariableToDisplay(value, nodeMap);
	};

	const handleSave = () => {
		// Validate agent_call step configuration
		if (step?.stepType === 'agent_call') {
			const validatedConfig = { ...config };

			// Ensure maxWaitTimeSeconds is either 0 (disabled) or within valid range
			if (validatedConfig.maxWaitTimeSeconds != null) {
				const timeout = validatedConfig.maxWaitTimeSeconds;
				if (timeout !== 0) {
					// If not disabled, enforce range
					if (timeout < 60) {
						validatedConfig.maxWaitTimeSeconds = 60;
					} else if (timeout > 3600) {
						validatedConfig.maxWaitTimeSeconds = 3600;
					}
				}
			}

			onSave?.({ config: validatedConfig, stepName });
			return;
		}

		// Config already contains UUIDs - no transformation needed
		onSave?.({ config, stepName });
	};

	// Pre-edit snapshot for the inline Revert action — `step` is the host's
	// stable selected-step reference and is never mutated by live commits.
	const originalConfig = useMemo<FlowStepConfig>(
		() =>
			step != null ? buildInitialStepConfig(step) : ({} as FlowStepConfig),
		[step],
	);
	const isDirty =
		JSON.stringify(config) !== JSON.stringify(originalConfig) ||
		stepName !== (step?.stepName ?? '');

	/** Discard inline edits, restoring the step to its pre-edit state. */
	const handleRevert = () => {
		if (step == null) {
			return;
		}
		setConfig(buildInitialStepConfig(step));
		setStepName(step.stepName ?? '');
	};

	// Stable wrapper for the start step's live-commit callback. Without
	// useCallback this is a new function every render, and StartNodeConfig-
	// Sidebar's commit watcher (which lists it as a dependency) would fire
	// endlessly — "Maximum update depth exceeded".
	const handleStartChange = useCallback(
		(startConfig: StartNodeConfig) => {
			onChange?.({ config: startConfig, stepName: 'Start' });
		},
		[onChange],
	);

	/**
	 * Data source field with support for nested types.
	 * When objectType or arrayType is set, the field can be expanded
	 * to show nested properties from the type registry.
	 */
	interface DataSourceField {
		key: string; // Storage key path: uuid.Order.orderId
		label: string; // Display label
		type: string; // Field type: string, number, object, array, etc.
		example?: string;
		displayKey?: string; // Display key path: Step Name.Order.Order ID
		objectType?: VariableTypeKey; // For object types, reference to type registry
		arrayType?: VariableTypeKey; // For array types, reference to type registry
		isExpandable?: boolean; // Whether this field can be expanded to show children
	}

	/**
	 * Build fields for a complex type from the type registry.
	 * This enables nested variable paths like Order.shippingAddress.city.
	 */
	const buildFieldsFromTypeRegistry = (
		baseKey: string,
		baseDisplayKey: string,
		typeKey: VariableTypeKey,
	): Array<DataSourceField> => {
		const typeDef = VARIABLE_TYPE_REGISTRY[typeKey];

		if (typeDef == null) {
			return [];
		}

		const fields: Array<DataSourceField> = [];

		for (const [propKey, propDef] of Object.entries(typeDef.properties)) {
			const fieldKey = `${baseKey}.${propKey}`;
			const displayLabel = propDef.label;
			const displayKeyPath = `${baseDisplayKey}.${displayLabel}`;

			const field: DataSourceField = {
				key: fieldKey,
				label: displayLabel,
				type: propDef.type,
				displayKey: displayKeyPath,
			};

			// Mark as expandable if it has nested types
			if (propDef.objectType != null) {
				field.objectType = propDef.objectType;
				field.isExpandable = true;
			}

			if (propDef.arrayType != null) {
				field.arrayType = propDef.arrayType;
				field.isExpandable = true;
			}

			fields.push(field);
		}

		return fields;
	};

	// Convert step context to available data sources for DynamicFieldValue
	// Memoized to prevent cascading re-renders in child components (especially RichTextEditor)
	const getAvailableDataSources = useCallback(() => {
		const sources: Array<{
			category: string;
			icon: typeof faPlay;
			fields: Array<DataSourceField>;
		}> = [];

		if (!stepContext) {
			return sources;
		}

		// Add Start variables if available
		if (stepContext.Start) {
			// Try to find Start node UUID from nodeMap first
			let startNodeId = Array.from(nodeMap.entries()).find(
				([id, info]) => info.stepName === 'Start',
			)?.[0];

			// If not found in nodeMap, use the UUID extracted from initial config (stored in state)
			if (startNodeId == null && startNodeUUID != null) {
				// Verify this UUID is likely the Start node by checking it's not in nodeMap as another step
				const isOtherStep = Array.from(nodeMap.values()).some(
					(info) => info.id === startNodeUUID && info.stepName !== 'Start',
				);

				if (!isOtherStep) {
					startNodeId = startNodeUUID;
				}
			}

			const startFields: Array<DataSourceField> = Object.entries(
				stepContext.Start.outputs || {},
			).map(([key, value]: [string, any]) => ({
				key: startNodeId ? `${startNodeId}.${key}` : `Start.${key}`,
				label: value.label || key,
				type: value.type || 'string',
				example: value.example || '',
				displayKey: `Start.${key}`,
				objectType: value.objectType,
				arrayType: value.arrayType,
				isExpandable: value.objectType != null || value.arrayType != null,
			}));

			if (startFields.length > 0) {
				sources.push({
					category: t('Start Variables'),
					icon: faPlay,
					fields: startFields,
				});
			}
		}

		// Add previous step outputs
		if (stepContext.previousSteps) {
			Object.entries(stepContext.previousSteps).forEach(
				([stepName, stepData]: [string, any]) => {
					// Find the node ID for this step name to use internal IDs
					const nodeId =
						Array.from(nodeMap.entries()).find(
							([id, info]) => info.stepName === stepName,
						)?.[0] || stepName;

					const stepFields: Array<DataSourceField> = Object.entries(
						stepData.outputs || {},
					).map(([key, value]: [string, any]) => {
						const field: DataSourceField = {
							key: `${nodeId}.${key}`,
							label: value.label || key,
							type: value.type || 'string',
							example: value.example || '',
							displayKey: `${stepName}.${key}`,
						};

						// Include type information for complex types
						if (value.objectType != null) {
							field.objectType = value.objectType as VariableTypeKey;
							field.isExpandable = true;
						}

						if (value.arrayType != null) {
							field.arrayType = value.arrayType as VariableTypeKey;
							field.isExpandable = true;
						}

						return field;
					});

					if (stepFields.length > 0) {
						// Choose icon based on step type
						let icon = faGears; // default
						const stepType = stepData.stepType;

						if (stepType === 'send_email') {
							icon = faEnvelope;
						} else if (stepType === 'agent' || stepType === 'agent_call') {
							icon = faRobot;
						} else if (stepType === 'action') {
							icon = faWrench;
						} else if (stepType === 'delay') {
							icon = faClock;
						} else if (stepType === 'condition') {
							icon = faCodeBranch;
						}

						sources.push({
							category: stepName,
							icon,
							fields: stepFields,
						});
					}
				},
			);
		}

		// Add entity data fields if available
		if (stepContext.entityData) {
			// Check if entityData has a fields property (from server response)
			const fieldsToProcess =
				stepContext.entityData.fields || stepContext.entityData;

			const entityFields: Array<DataSourceField> = Object.entries(
				fieldsToProcess,
			).map(([key, value]: [string, any]) => ({
				key: `entityData.${key}`,
				label: value.label || key,
				type: value.type || 'string',
				example: value.example || '',
				objectType: value.objectType,
				arrayType: value.arrayType,
				isExpandable: value.objectType != null || value.arrayType != null,
			}));

			if (entityFields.length > 0) {
				sources.push({
					category: t('Entity Data'),
					icon: faDatabase,
					fields: entityFields,
				});
			}
		}

		// Add system variables from context if available
		if (stepContext.systemVariables) {
			const systemFields: Array<DataSourceField> = Object.entries(
				stepContext.systemVariables,
			).map(([key, value]: [string, any]) => ({
				key,
				label: value.label || key,
				type: value.type || 'string',
				example: value.example || '',
			}));

			if (systemFields.length > 0) {
				sources.push({
					category: t('System Variables'),
					icon: faCogs,
					fields: systemFields,
				});
			}
		}

		return sources;
	}, [stepContext, nodeMap, startNodeUUID, t]);

	const renderTemplateConfig = () => {
		const templateOptions = templates.map((template) => ({
			label: template.name,
			value: template.id.toString(),
		}));

		return (
			<div className="space-y-4">
				<div>
					<label
						className={`text-sm font-medium mb-2 block ${colors.classes.textPrimary}`}
					>
						Select Email Template
					</label>
					<Select
						options={templateOptions}
						value={config.templateId?.toString()}
						onChange={(value: string) => {
							setConfig({ ...config, templateId: parseInt(value) });
						}}
						placeholder="Choose a template"
					/>
				</div>
			</div>
		);
	};

	const renderSendEmailConfig = () => (
		<EmailStepConfig
			config={config}
			setConfig={setConfig}
			getAvailableDataSources={getAvailableDataSources}
			getDisplayText={getDisplayText}
			verifiedDomains={verifiedDomains}
			domainsLoading={domainsLoading}
			fallbackFrom={fallbackFrom}
		/>
	);

	const renderSendSmsConfig = () => (
		<SmsStepConfig
			config={config}
			setConfig={setConfig}
			getAvailableDataSources={getAvailableDataSources}
			getDisplayText={getDisplayText}
			provisionedPhones={provisionedPhones}
			phonesLoading={phonesLoading}
		/>
	);

	const renderDelayConfig = () => {
		// Convert duration/unit to delayMs for 'relative' type
		const convertToMs = (value: number, unit: string) => {
			switch (unit) {
				case 'seconds':
					return value * 1000;
				case 'minutes':
					return value * 60 * 1000;
				case 'hours':
					return value * 60 * 60 * 1000;
				case 'days':
					return value * 24 * 60 * 60 * 1000;
				default:
					return value * 60 * 1000; // Default to minutes
			}
		};

		return (
			<div className="space-y-4">
				<div>
					<h3
						className={`text-sm font-medium mb-2 ${colors.classes.textPrimary}`}
					>
						{t('Delay Duration')}
					</h3>
					<p className={`text-xs mb-3 ${colors.classes.textSecondary}`}>
						{t('Set the wait time before the next step')}
					</p>

					<div className="flex gap-3">
						{/* Duration Input */}
						<div className="flex-1">
							<label
								className={`text-[13px] mb-1 block ${colors.classes.textSecondary}`}
							>
								{t('Duration')}
							</label>
							<Input
								type="number"
								value={config.relativeValue || 1}
								onChange={(e) => {
									const value = parseInt(e.target.value) || 0;
									setConfig({
										...config,
										delayType: 'relative',
										relativeValue: value,
										relativeUnit: config.relativeUnit || 'minutes',
									});
								}}
								min={0}
								placeholder={t('Enter duration')}
							/>
						</div>

						{/* Unit Select */}
						<div className="flex-1">
							<label
								className={`text-[13px] mb-1 block ${colors.classes.textSecondary}`}
							>
								{t('Unit')}
							</label>
							<Select
								value={config.relativeUnit ?? 'minutes'}
								onChange={(value) => {
									setConfig({
										...config,
										delayType: 'relative',
										relativeValue: config.relativeValue || 1,
										relativeUnit: value,
									});
								}}
								options={[
									{ value: 'seconds', label: t('Seconds') },
									{ value: 'minutes', label: t('Minutes') },
									{ value: 'hours', label: t('Hours') },
									{ value: 'days', label: t('Days') },
								]}
							/>
						</div>
					</div>

					{/* Total duration display */}
					<div className={`mt-4 p-3 rounded-md ${colors.classes.bgSecondary}`}>
						<p
							className={`text-[13px] font-medium ${colors.classes.textPrimary}`}
						>
							{t('Total wait time')}:{' '}
							{(() => {
								const duration = config.relativeValue || 1;
								const unit = config.relativeUnit || 'minutes';

								let totalMs = 0;
								switch (unit) {
									case 'seconds':
										totalMs = duration * 1000;
										break;
									case 'minutes':
										totalMs = duration * 60 * 1000;
										break;
									case 'hours':
										totalMs = duration * 60 * 60 * 1000;
										break;
									case 'days':
										totalMs = duration * 24 * 60 * 60 * 1000;
										break;
								}

								const days = Math.floor(totalMs / (24 * 60 * 60 * 1000));
								const hours = Math.floor(
									(totalMs % (24 * 60 * 60 * 1000)) / (60 * 60 * 1000),
								);
								const minutes = Math.floor(
									(totalMs % (60 * 60 * 1000)) / (60 * 1000),
								);
								const seconds = Math.floor((totalMs % (60 * 1000)) / 1000);

								const parts = [];
								if (days > 0) {
									parts.push(`${days} ${t('day')}${days !== 1 ? 's' : ''}`);
								}
								if (hours > 0) {
									parts.push(`${hours} ${t('hour')}${hours !== 1 ? 's' : ''}`);
								}
								if (minutes > 0) {
									parts.push(
										`${minutes} ${t('minute')}${minutes !== 1 ? 's' : ''}`,
									);
								}
								if (seconds > 0) {
									parts.push(
										`${seconds} ${t('second')}${seconds !== 1 ? 's' : ''}`,
									);
								}

								return parts.length > 0 ? parts.join(', ') : t('No delay');
							})()}
						</p>
					</div>
				</div>
			</div>
		);
	};

	const renderConditionConfig = () => {
		const conditions = config.conditions || [];

		// Helper to generate new UUID
		const generateUUID = () =>
			'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
				const r = (Math.random() * 16) | 0;
				const v = c === 'x' ? r : (r & 0x3) | 0x8;
				return v.toString(16);
			});

		// Helper functions for JsonLogic
		const cleanVariablePath = (value: string): string =>
			// Remove {{ }} wrapper if present
			value.replace(/^\{\{|\}\}$/g, '');
		const createJsonLogicRule = (
			left: { type: string; value: string },
			operator: string,
			right: { type: string; value: string },
		) => {
			const leftVar =
				left.type === 'dynamic'
					? { var: cleanVariablePath(left.value) }
					: left.value;
			const rightVar =
				right.type === 'dynamic'
					? { var: cleanVariablePath(right.value) }
					: right.value;

			switch (operator) {
				case 'equals':
					return { '==': [leftVar, rightVar] };
				case 'not_equals':
					return { '!=': [leftVar, rightVar] };
				case 'contains':
					return { in: [rightVar, leftVar] };
				case 'not_contains':
					return { '!': [{ in: [rightVar, leftVar] }] };
				case 'starts_with':
					return {
						'===': [{ substr: [leftVar, 0, { length: rightVar }] }, rightVar],
					};
				case 'ends_with':
					return {
						'===': [
							{
								substr: [
									leftVar,
									{ '-': [{ length: leftVar }, { length: rightVar }] },
								],
							},
							rightVar,
						],
					};
				case 'greater_than':
					return { '>': [leftVar, rightVar] };
				case 'less_than':
					return { '<': [leftVar, rightVar] };
				case 'greater_or_equal':
					return { '>=': [leftVar, rightVar] };
				case 'less_or_equal':
					return { '<=': [leftVar, rightVar] };
				case 'is_empty':
					return { or: [{ '==': [leftVar, null] }, { '==': [leftVar, ''] }] };
				case 'is_not_empty':
					return { and: [{ '!=': [leftVar, null] }, { '!=': [leftVar, ''] }] };
				case 'in':
					const rightArray =
						right.type === 'dynamic'
							? { var: right.value }
							: right.value.split(',').map((s) => s.trim());
					return { in: [leftVar, rightArray] };
				case 'not_in':
					const rightArrayNotIn =
						right.type === 'dynamic'
							? { var: right.value }
							: right.value.split(',').map((s) => s.trim());
					return { '!': [{ in: [leftVar, rightArrayNotIn] }] };
				default:
					return { '==': [leftVar, rightVar] };
			}
		};

		const parseJsonLogicRule = (rule: any) => {
			// Simple parser for basic JsonLogic rules to populate UI form
			if (!rule || typeof rule !== 'object') {
				return {
					left: { type: 'dynamic', value: '' },
					operator: 'equals',
					right: { type: 'static', value: '' },
				};
			}

			const operator = Object.keys(rule)[0];
			if (operator == null) {
				return {
					left: { type: 'dynamic', value: '' },
					operator: 'equals',
					right: { type: 'static', value: '' },
				};
			}
			const operands = rule[operator];

			if (!Array.isArray(operands) || operands.length < 2) {
				return {
					left: { type: 'dynamic', value: '' },
					operator: 'equals',
					right: { type: 'static', value: '' },
				};
			}

			const leftOperand = operands[0];
			const rightOperand = operands[1];

			const left =
				leftOperand &&
				typeof leftOperand === 'object' &&
				leftOperand.var !== undefined
					? {
							type: 'dynamic',
							value: leftOperand.var ? `{{${leftOperand.var}}}` : '',
						}
					: { type: 'static', value: String(leftOperand || '') };

			const right =
				rightOperand &&
				typeof rightOperand === 'object' &&
				rightOperand.var !== undefined
					? {
							type: 'dynamic',
							value: rightOperand.var ? `{{${rightOperand.var}}}` : '',
						}
					: { type: 'static', value: String(rightOperand || '') };

			// Map JsonLogic operators back to our UI operators
			let uiOperator = 'equals';
			switch (operator) {
				case '==':
					uiOperator = 'equals';
					break;
				case '!=':
					uiOperator = 'not_equals';
					break;
				case '>':
					uiOperator = 'greater_than';
					break;
				case '<':
					uiOperator = 'less_than';
					break;
				case '>=':
					uiOperator = 'greater_or_equal';
					break;
				case '<=':
					uiOperator = 'less_or_equal';
					break;
				case 'in':
					uiOperator = 'contains';
					break;
			}

			return { left, operator: uiOperator, right };
		};

		const addCondition = () => {
			const newCondition = {
				id: `cond-${Date.now()}`,
				key: generateUUID(),
				label: `Condition ${conditions.length + 1}`,
				priority: conditions.length + 1,
				rule: {
					'==': [{ var: '' }, ''],
				},
			};

			setConfig({
				...config,
				conditions: [...conditions, newCondition],
				evaluationMode: config.evaluationMode || 'first_match',
			});
		};

		const removeCondition = (index: number) => {
			const updatedConditions = conditions.filter(
				(_: any, i: number) => i !== index,
			);
			// Renumber priorities
			const renumberedConditions = updatedConditions.map(
				(c: any, i: number) => ({
					...c,
					priority: i + 1,
				}),
			);

			setConfig({ ...config, conditions: renumberedConditions });
		};

		const updateCondition = (index: number, updates: any) => {
			const updatedConditions = conditions.map((c: any, i: number) => {
				if (i !== index) {
					return c;
				}

				// Start with the existing condition
				const updatedCondition = { ...c };

				// Handle label updates directly
				if (updates.label !== undefined) {
					updatedCondition.label = updates.label;
				}

				// If any rule-affecting properties changed, regenerate the JsonLogic rule
				if (
					updates.left !== undefined ||
					updates.operator !== undefined ||
					updates.right !== undefined
				) {
					// Parse current condition to get UI values
					const currentUI = parseJsonLogicRule(c.rule);

					// Apply updates to UI values
					const left =
						updates.left !== undefined ? updates.left : currentUI.left;
					const operator =
						updates.operator !== undefined
							? updates.operator
							: currentUI.operator;
					const right =
						updates.right !== undefined ? updates.right : currentUI.right;

					// Generate new JsonLogic rule
					updatedCondition.rule = createJsonLogicRule(left, operator, right);
				}

				return updatedCondition;
			});

			setConfig({ ...config, conditions: updatedConditions });
		};

		const operatorOptions = [
			{ value: 'equals', label: t('Equals') },
			{ value: 'not_equals', label: t('Not Equals') },
			{ value: 'contains', label: t('Contains') },
			{ value: 'not_contains', label: t('Does Not Contain') },
			{ value: 'starts_with', label: t('Starts With') },
			{ value: 'ends_with', label: t('Ends With') },
			{ value: 'greater_than', label: t('Greater Than') },
			{ value: 'less_than', label: t('Less Than') },
			{ value: 'greater_or_equal', label: t('Greater Than or Equal') },
			{ value: 'less_or_equal', label: t('Less Than or Equal') },
			{ value: 'is_empty', label: t('Is Empty') },
			{ value: 'is_not_empty', label: t('Is Not Empty') },
			{ value: 'regex_match', label: t('Matches Pattern') },
			{ value: 'in', label: t('In List') },
			{ value: 'not_in', label: t('Not In List') },
		];

		return (
			<div className="space-y-6">
				{/* Header */}
				<div className="flex justify-between items-center">
					<div>
						<h3 className={`text-sm font-medium ${colors.classes.textPrimary}`}>
							{t('Conditions')}
						</h3>
						<p className={`text-xs mt-1 ${colors.classes.textSecondary}`}>
							{t('Define conditions that route to different branches')}
						</p>
					</div>
					<Button onClick={addCondition} variant="secondary" size="sm">
						{t('Add Condition')}
					</Button>
				</div>

				{/* Conditions List */}
				{conditions.map((condition: any, index: number) => {
					// Parse JsonLogic rule to get UI values
					const uiValues = parseJsonLogicRule(condition.rule);

					return (
						<div
							key={condition.id}
							className={`p-4 rounded-lg border space-y-4 ${colors.classes.bgSecondary} ${colors.classes.borderDefault}`}
						>
							{/* Condition Header */}
							<div className="flex justify-between items-start">
								<div className="flex-1 space-y-2">
									<div className="flex items-center gap-2">
										<span
											className={`text-xs font-mono px-2 py-1 rounded ${colors.classes.bgTertiary} ${colors.classes.textSecondary}`}
										>
											#{condition.priority}
										</span>
										<Input
											value={condition.label}
											onChange={(e) => {
												updateCondition(index, { label: e.target.value });
											}}
											placeholder={t('Condition Label')}
											className="flex-1"
										/>
									</div>
									<div className={`text-xs ${colors.classes.textSecondary}`}>
										{t('Key')}:{' '}
										<span className="font-mono">
											{condition.key.substring(0, 8)}...
										</span>
									</div>
								</div>
								<Button
									onClick={() => {
										removeCondition(index);
									}}
									variant="ghost"
									size="sm"
									className="ml-2 text-danger hover:text-danger"
								>
									{t('Remove')}
								</Button>
							</div>

							{/* Left Value */}
							<div>
								<label
									className={`text-xs font-medium mb-1 block ${colors.classes.textPrimary}`}
								>
									{t('Compare Value')}
								</label>
								<DynamicFieldValue
									label=""
									value={uiValues.left.value}
									onChange={(value) => {
										const isDynamic =
											value && value.startsWith('{{') && value.endsWith('}}');
										updateCondition(index, {
											left: { type: isDynamic ? 'dynamic' : 'static', value },
										});
									}}
									dataType="string"
									placeholder={t('Select field or enter value')}
									availableData={getAvailableDataSources()}
									displayTransform={getDisplayText}
								/>
							</div>

							{/* Operator */}
							<div>
								<label
									className={`text-xs font-medium mb-1 block ${colors.classes.textPrimary}`}
								>
									{t('Operator')}
								</label>
								<Select
									options={operatorOptions}
									value={uiValues.operator}
									onChange={(value: string) => {
										updateCondition(index, { operator: value });
									}}
								/>
							</div>

							{/* Right Value (if operator requires it) */}
							{!['is_empty', 'is_not_empty'].includes(uiValues.operator) && (
								<div>
									<label
										className={`text-xs font-medium mb-1 block ${colors.classes.textPrimary}`}
									>
										{t('To Value')}
									</label>
									<DynamicFieldValue
										label=""
										value={uiValues.right.value}
										onChange={(value) => {
											const isDynamic =
												value && value.startsWith('{{') && value.endsWith('}}');
											updateCondition(index, {
												right: {
													type: isDynamic ? 'dynamic' : 'static',
													value,
												},
											});
										}}
										dataType="string"
										placeholder={t('Enter comparison value')}
										availableData={getAvailableDataSources()}
										displayTransform={getDisplayText}
									/>
								</div>
							)}

							{/* Condition Preview */}
							<div
								className={`p-2 rounded text-xs font-mono ${colors.classes.bgTertiary} ${colors.classes.textSecondary}`}
							>
								{t('If')} {uiValues.left.value || '___'}{' '}
								{operatorOptions.find((op) => op.value === uiValues.operator)
									?.label || uiValues.operator}{' '}
								{!['is_empty', 'is_not_empty'].includes(uiValues.operator)
									? uiValues.right.value || '___'
									: ''}{' '}
								{t('then')} → <strong>{condition.label}</strong>
							</div>
						</div>
					);
				})}

				{/* Empty State */}
				{conditions.length === 0 && (
					<div className={`text-center py-8 ${colors.classes.textSecondary}`}>
						<p>{t('No conditions configured')}</p>
						<p className="text-xs mt-1">
							{t('Add conditions to create routing branches')}
						</p>
					</div>
				)}

				{/* Evaluation Mode */}
				{conditions.length > 1 && (
					<div>
						<label
							className={`text-sm font-medium mb-2 block ${colors.classes.textPrimary}`}
						>
							{t('Evaluation Mode')}
						</label>
						<Select
							options={[
								{ value: 'first_match', label: t('First Match (recommended)') },
								{ value: 'all_matching', label: t('All Matching') },
							]}
							value={config.evaluationMode || 'first_match'}
							onChange={(value: string) => {
								setConfig({ ...config, evaluationMode: value });
							}}
						/>
						<div className={`text-xs mt-1 ${colors.classes.textSecondary}`}>
							{config.evaluationMode === 'first_match'
								? t('Stop at first condition that matches (typical behavior)')
								: t('Evaluate all conditions and use first match for routing')}
						</div>
					</div>
				)}

				{/* Configuration Summary */}
				{conditions.length > 0 && (
					<div className={`p-4 rounded-lg ${colors.classes.bgSecondary}`}>
						<h4
							className={`text-sm font-medium mb-2 ${colors.classes.textPrimary}`}
						>
							{t('Configuration Summary')}
						</h4>
						<div className="text-xs space-y-1">
							<div className={colors.classes.textSecondary}>
								{t('Conditions')}: {conditions.length}
							</div>
							<div className={colors.classes.textSecondary}>
								{t('Branches')}:{' '}
								{conditions.map((c: any) => c.label).join(', ')}
							</div>
						</div>
					</div>
				)}
			</div>
		);
	};

	const renderAgentConfig = () => (
		<AgentStepConfig config={config} setConfig={setConfig} stepType="agent" />
	);

	const renderEvaluateConfig = () => (
		<EvaluateStepConfig
			config={config}
			setConfig={setConfig}
			getAvailableDataSources={getAvailableDataSources}
			getDisplayText={getDisplayText}
		/>
	);

	const renderEmailIngestConfig = (): React.JSX.Element => (
		<EmailIngestStepConfig
			config={config}
			setConfig={setConfig}
			getAvailableDataSources={getAvailableDataSources}
			getDisplayText={getDisplayText}
		/>
	);

	const renderProductMatchConfig = (): React.JSX.Element => (
		<ProductMatchStepConfig
			config={config}
			setConfig={setConfig}
			getAvailableDataSources={getAvailableDataSources}
			getDisplayText={getDisplayText}
		/>
	);

	const renderExternalLookupConfig = (): React.JSX.Element => (
		<ExternalLookupStepConfig
			config={config}
			setConfig={setConfig}
			getAvailableDataSources={getAvailableDataSources}
			getDisplayText={getDisplayText}
		/>
	);

	const renderAgentCallConfig = () => {
		// Ensure boolean value for waitForCompletion (default true if undefined)
		const waitForCompletionValue =
			config.waitForCompletion === false ? 'false' : 'true';
		// buildInitialStepConfig defaults this to 0 for agent_call steps.
		const maxWaitTimeSeconds = config.maxWaitTimeSeconds ?? 0;

		// Get agent input variables for mapping
		const getAgentInputVariables = (): VariableMappingVariable[] => {
			if (!selectedAgentForMapping?.metadata?.inputVariables) {
				return [];
			}
			return selectedAgentForMapping.metadata.inputVariables.map((v: any) => ({
				name: v.name,
				type: v.type,
				required: v.required,
				description: v.label || v.name,
				defaultValue: v.defaultValue,
			}));
		};

		// Handle saving input variable mapping
		const handleSaveInputMapping = (mapping: Record<string, string>) => {
			setConfig({
				...config,
				inputVariables: mapping,
			});
			setShowAgentInputMapping(false);
		};

		return (
			<div className="space-y-6">
				{/* Agent Selection with Dynamic Search */}
				<AgentStepConfig
					config={config}
					setConfig={setConfig}
					stepType="agent_call"
					onOpenVariableMapping={() => {
						setShowAgentInputMapping(true);
					}}
				/>

				{/* To Phone Number */}
				<DynamicFieldValue
					label={t('To Phone Number')}
					value={config.toNumber || ''}
					onChange={(value) => {
						setConfig({ ...config, toNumber: value });
					}}
					dataType="string"
					placeholder={t('Enter phone number or select from data')}
					required={true}
					description={t(
						'Phone number to call. Use dynamic values to call different numbers based on execution data.',
					)}
					availableData={getAvailableDataSources()}
					displayTransform={getDisplayText}
				/>

				{/* From Phone Number */}
				<PhoneNumberField
					label={t('From Phone Number')}
					value={config.fromNumber || ''}
					onChange={(value) => {
						setConfig({ ...config, fromNumber: value });
					}}
					required={true}
					description={t(
						'Phone number to call from. Select a provisioned number or use a dynamic value.',
					)}
					availableData={getAvailableDataSources()}
					provisionedPhones={provisionedPhones}
					phonesLoading={phonesLoading}
				/>

				{/* Wait for Completion */}
				<div className="flex items-center justify-between">
					<div>
						<label
							className={`text-sm font-medium ${colors.classes.textPrimary}`}
						>
							{t('Wait for Call Completion')}
						</label>
						<div className={`text-xs mt-1 ${colors.classes.textSecondary}`}>
							{waitForCompletionValue === 'true'
								? t('Flow will wait for call to complete before continuing')
								: t('Flow will continue immediately after initiating call')}
						</div>
					</div>
					<Button
						variant="ghost"
						size="sm"
						onClick={() => {
							const newValue =
								waitForCompletionValue === 'true' ? 'false' : 'true';
							const boolValue = newValue === 'true';
							console.debug('Setting waitForCompletion to:', boolValue);
							setConfig({
								...config,
								waitForCompletion: boolValue,
							});
						}}
						className="flex items-center gap-2"
						title={
							waitForCompletionValue === 'true'
								? t('Switch to Continue Immediately')
								: t('Switch to Wait for Completion')
						}
					>
						<Icon
							icon={
								waitForCompletionValue === 'true' ? faToggleOn : faToggleOff
							}
							className={
								waitForCompletionValue === 'true'
									? colors.classes.statusSuccess
									: colors.classes.textSecondary
							}
							size={16}
						/>
						<span className="text-xs font-medium">
							{waitForCompletionValue === 'true' ? t('Wait') : t('Continue')}
						</span>
					</Button>
				</div>

				{/* Max Wait Time (if waiting) */}
				{waitForCompletionValue === 'true' && (
					<>
						{/* Timeout Toggle */}
						<div className="flex items-center justify-between">
							<div>
								<label
									className={`text-sm font-medium ${colors.classes.textPrimary}`}
								>
									{t('Set Maximum Wait Time')}
								</label>
								<div className={`text-xs mt-1 ${colors.classes.textSecondary}`}>
									{maxWaitTimeSeconds > 0
										? t('Call will timeout after specified duration')
										: t('Call will wait indefinitely until complete')}
								</div>
							</div>
							<Button
								variant="ghost"
								size="sm"
								onClick={() => {
									if (maxWaitTimeSeconds > 0) {
										// Disable timeout (wait indefinitely)
										setConfig({
											...config,
											maxWaitTimeSeconds: 0,
										});
									} else {
										// Enable timeout with default value
										setConfig({
											...config,
											maxWaitTimeSeconds: 600, // Default to 10 minutes
										});
									}
								}}
								className="flex items-center gap-2"
								title={
									maxWaitTimeSeconds > 0
										? t('Disable timeout')
										: t('Enable timeout')
								}
							>
								<Icon
									icon={maxWaitTimeSeconds > 0 ? faToggleOn : faToggleOff}
									className={
										maxWaitTimeSeconds > 0
											? colors.classes.statusSuccess
											: colors.classes.textSecondary
									}
									size={16}
								/>
								<span className="text-xs font-medium">
									{maxWaitTimeSeconds > 0 ? t('Timeout') : t('No Timeout')}
								</span>
							</Button>
						</div>

						{/* Timeout Duration (if enabled) */}
						{maxWaitTimeSeconds > 0 && (
							<div>
								<label
									className={`text-sm font-medium mb-2 block ${colors.classes.textPrimary}`}
								>
									{t('Timeout Duration (seconds)')}
								</label>
								<Input
									type="number"
									value={maxWaitTimeSeconds}
									onChange={(e) => {
										const numValue = parseInt(e.target.value) || 0;
										console.debug('Setting maxWaitTimeSeconds to:', numValue);
										setConfig({
											...config,
											maxWaitTimeSeconds: numValue,
										});
									}}
									placeholder={t('600')}
								/>
								<div className={`text-xs mt-1 ${colors.classes.textSecondary}`}>
									{t(
										'How long to wait before timing out (must be between 60-3600 seconds)',
									)}
								</div>
								{/* Show validation error if out of range */}
								{maxWaitTimeSeconds > 0 &&
									(maxWaitTimeSeconds < 60 || maxWaitTimeSeconds > 3600) && (
										<div className="text-xs mt-1 text-danger">
											{maxWaitTimeSeconds < 60
												? t('Timeout must be at least 60 seconds')
												: t('Timeout cannot exceed 3600 seconds (1 hour)')}
										</div>
									)}
							</div>
						)}
					</>
				)}

				{/* Input Variables Mapping Modal */}
				{showAgentInputMapping && selectedAgentForMapping && (
					<Modal
						isOpen={showAgentInputMapping}
						onClose={() => {
							setShowAgentInputMapping(false);
						}}
						title={t('Map Agent Input Variables')}
					>
						<VariableMapping
							variables={getAgentInputVariables()}
							mapping={
								// agent_call inputVariables are written only by
								// handleSaveInputMapping, which stores string→string.
								(config.inputVariables ?? {}) as Record<string, string>
							}
							onSave={handleSaveInputMapping}
							onCancel={() => {
								setShowAgentInputMapping(false);
							}}
							useDynamicFieldValue={true}
							availableData={getAvailableDataSources()}
							displayTransform={getDisplayText}
							enableAutoMap={false}
							description={t(
								'Map automation flow data to agent input variables. Each variable will be passed to the agent when the call is initiated.',
							)}
						/>
					</Modal>
				)}
			</div>
		);
	};

	const renderParallelConfig = () => {
		const branches = config.branches || {};
		const branchKeys = Object.keys(branches);

		// Helper to generate new UUID
		const generateUUID = () =>
			'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
				const r = (Math.random() * 16) | 0;
				const v = c === 'x' ? r : (r & 0x3) | 0x8;
				return v.toString(16);
			});

		const addBranch = () => {
			const branchKey = generateUUID();
			const newBranch = {
				branchName: `Branch ${branchKeys.length + 1}`,
				waitForCompletion: true,
				failureMode: 'fail_parent' as const,
			};

			setConfig({
				...config,
				branches: {
					...branches,
					[branchKey]: newBranch,
				},
			});
		};

		const removeBranch = (branchKey: string) => {
			const updatedBranches = { ...branches };
			delete updatedBranches[branchKey];
			setConfig({ ...config, branches: updatedBranches });
		};

		const updateBranch = (branchKey: string, updates: any) => {
			setConfig({
				...config,
				branches: {
					...branches,
					[branchKey]: {
						...branches[branchKey],
						...updates,
					},
				},
			});
		};

		return (
			<div className="space-y-4">
				<div>
					<h3
						className={`text-sm font-medium mb-2 ${colors.classes.textPrimary}`}
					>
						{t('Parallel Branches')}
					</h3>
					<p className={`text-xs mb-3 ${colors.classes.textSecondary}`}>
						{t('Configure branches that will execute in parallel')}
					</p>

					{branchKeys.length === 0 && (
						<div
							className={`p-4 rounded-md ${colors.classes.bgSecondary} text-center`}
						>
							<p className={`text-sm ${colors.classes.textSecondary}`}>
								{t(
									'No branches configured. Add at least one branch to continue.',
								)}
							</p>
						</div>
					)}

					{branchKeys.map((branchKey, index) => {
						const branch = branches[branchKey];

						return (
							<div
								key={branchKey}
								className={`p-4 rounded-md mb-3 ${colors.classes.bgSecondary}`}
							>
								<div className="flex items-center justify-between mb-3">
									<span
										className={`text-sm font-medium ${colors.classes.textPrimary}`}
									>
										{t('Branch')} {index + 1}
									</span>
									<Button
										variant="ghost"
										onClick={() => {
											removeBranch(branchKey);
										}}
										className="text-danger hover:text-danger"
									>
										<Icon icon={faTrash} />
									</Button>
								</div>

								<div className="space-y-3">
									<div>
										<label
											className={`text-[13px] mb-1 block ${colors.classes.textSecondary}`}
										>
											{t('Branch Name')}
										</label>
										<Input
											value={branch?.branchName || ''}
											onChange={(e) => {
												updateBranch(branchKey, { branchName: e.target.value });
											}}
											placeholder={t('Enter branch name')}
										/>
									</div>

									<div className="flex items-center gap-2">
										<input
											type="checkbox"
											id={`wait-${branchKey}`}
											checked={branch?.waitForCompletion ?? true}
											onChange={(e) => {
												updateBranch(branchKey, {
													waitForCompletion: e.target.checked,
												});
											}}
											className="rounded"
										/>
										<label
											htmlFor={`wait-${branchKey}`}
											className={`text-sm cursor-pointer ${colors.classes.textPrimary}`}
										>
											{t('Wait for branch completion')}
										</label>
									</div>

									<div>
										<label
											className={`text-[13px] mb-1 block ${colors.classes.textSecondary}`}
										>
											{t('Failure Mode')}
										</label>
										<Select
											value={branch?.failureMode ?? 'fail_parent'}
											onChange={(value) => {
												updateBranch(branchKey, {
													failureMode: value,
												});
											}}
											options={[
												{
													value: 'fail_parent',
													label: t('Fail entire parallel step'),
												},
												{ value: 'ignore', label: t('Continue regardless') },
												{
													value: 'mark_only',
													label: t('Mark failure but continue'),
												},
											]}
										/>
									</div>

									{/* Branch target selection - only in list builder mode */}
									{allSteps != null && (
										<div>
											<label
												className={`text-[13px] mb-1 block ${colors.classes.textSecondary}`}
											>
												{t('Branch Target')}
											</label>
											<StepReferenceSelector
												steps={allSteps}
												currentStepId={step?.id}
												value={branch?.targetStepId ?? null}
												onChange={(targetStepId) => {
													updateBranch(branchKey, { targetStepId });
												}}
												placeholder={t('Continue to next step or select...')}
											/>
											<p
												className={`text-xs mt-1 ${colors.classes.textSecondary}`}
											>
												{t(
													'Leave empty to continue with new steps in this branch',
												)}
											</p>
										</div>
									)}
								</div>
							</div>
						);
					})}

					<Button onClick={addBranch} variant="outline" className="w-full">
						<Icon icon={faPlus} className="mr-2" />
						{t('Add Branch')}
					</Button>
				</div>
			</div>
		);
	};

	const renderWaitForEventConfig = () => (
		<WaitForEventStepConfig
			config={config}
			setConfig={setConfig}
			getAvailableDataSources={getAvailableDataSources}
			getDisplayText={getDisplayText}
		/>
	);

	const renderLoadDataConfig = () => {
		// Find the selected data loader type from server data
		const selectedLoaderType = dataLoaderTypes.find(
			(loader) => loader.dataType === config.dataType,
		);

		// Get the appropriate entity ID field name based on data type
		const getEntityIdLabel = () => {
			if (selectedLoaderType != null) {
				return t(`${selectedLoaderType.name} ID`);
			}

			return t('Entity ID');
		};

		// Build output fields from the server's outputSchema
		const getOutputFields = (): Array<{
			name: string;
			type: string;
			label: string;
			description?: string;
		}> => {
			if (selectedLoaderType?.outputSchema == null) {
				return [];
			}

			const schema = selectedLoaderType.outputSchema;

			return Object.entries(schema).map(([fieldName, fieldDef]) => {
				const def = fieldDef as {
					type?: string;
					label?: string;
					description?: string;
				};

				return {
					name: fieldName,
					type: def.type ?? 'unknown',
					label: t(def.label ?? fieldName),
					description: def.description,
				};
			});
		};

		const outputFields = getOutputFields();

		return (
			<div className="space-y-4">
				<div>
					<h3
						className={`text-sm font-medium mb-2 ${colors.classes.textPrimary}`}
					>
						{t('Data Type')}
					</h3>
					<p className={`text-xs mb-3 ${colors.classes.textSecondary}`}>
						{t('Select the type of data to load')}
					</p>
					<Select
						value={config.dataType ?? ''}
						onChange={(value) => {
							setConfig({ ...config, dataType: value });
						}}
						placeholder={t('Select data type...')}
						options={dataLoaderTypes.map((loaderType) => ({
							value: loaderType.dataType,
							label: t(loaderType.name),
						}))}
					/>
				</div>

				{config.dataType != null && config.dataType !== '' && (
					<>
						<DynamicFieldValue
							label={getEntityIdLabel()}
							value={config.entityId?.toString() ?? ''}
							onChange={(value) => {
								setConfig({ ...config, entityId: value });
							}}
							dataType="string"
							placeholder={t('Enter ID or select from data')}
							required={true}
							description={t(
								'The ID of the entity to load. Use a dynamic value to load different entities based on trigger or previous step data.',
							)}
							availableData={getAvailableDataSources()}
							displayTransform={getDisplayText}
						/>

						{/* Output Properties Preview */}
						{outputFields.length > 0 && (
							<div
								className={`mt-4 p-3 rounded-md border ${colors.classes.borderDefault} ${colors.classes.bgTertiary}`}
							>
								<h4
									className={`text-sm font-medium mb-2 ${colors.classes.textPrimary}`}
								>
									{t('Output Properties')}
								</h4>
								<p className={`text-xs mb-3 ${colors.classes.textSecondary}`}>
									{t('These fields will be available in subsequent steps via')}{' '}
									<code className="text-xs">{'{{stepName.fieldName}}'}</code>
								</p>
								<div className="grid grid-cols-1 gap-2">
									{outputFields.map((field) => (
										<div
											key={field.name}
											className={`text-xs ${colors.classes.textSecondary} flex items-center gap-2`}
										>
											<span
												className={`px-1.5 py-0.5 rounded text-[10px] font-mono ${colors.classes.bgSecondary} shrink-0`}
											>
												{field.type}
											</span>
											<span
												className={`${colors.classes.textPrimary} font-medium`}
											>
												{field.label}
											</span>
											<span
												className={`font-mono text-[10px] ${colors.classes.textMuted}`}
											>
												({field.name})
											</span>
											{field.description != null && (
												<span className={colors.classes.textSecondary}>
													- {t(field.description)}
												</span>
											)}
										</div>
									))}
								</div>
							</div>
						)}
					</>
				)}
			</div>
		);
	};

	const renderConfig = () => {
		if (!step) {
			return null;
		}

		switch (step.stepType) {
			case 'template':
				return renderTemplateConfig();
			case 'send_email':
				return renderSendEmailConfig();
			case 'send_sms':
				return renderSendSmsConfig();
			case 'delay':
				return renderDelayConfig();
			case 'condition':
				return renderConditionConfig();
			case 'parallel':
				return renderParallelConfig();
			case 'wait_for_event':
				return renderWaitForEventConfig();
			case 'agent':
				return renderAgentConfig();
			case 'agent_call':
				return renderAgentCallConfig();
			case 'evaluate':
				return renderEvaluateConfig();
			case 'email_ingest':
				return renderEmailIngestConfig();
			case 'product_match':
				return renderProductMatchConfig();
			case 'external_lookup':
				return renderExternalLookupConfig();
			case 'load_data':
				return renderLoadDataConfig();
			case 'client_bulk_action':
				return (
					<ClientBulkActionStepConfig config={config} setConfig={setConfig} />
				);
			default:
				return (
					<p className={`text-center p-5 ${colors.classes.textSecondary}`}>
						{t('Configuration for')} {step.stepType} {t('steps coming soon')}
					</p>
				);
		}
	};

	if (!step) {
		return null;
	}

	// Handle start step with its own modal
	if (step.stepType === 'start') {
		return (
			<StartNodeConfigSidebar
				visible={visible}
				inline={inline}
				onCancel={onCancel}
				onChange={onChange != null ? handleStartChange : undefined}
				onSave={(startConfig) => {
					onSave?.({
						config: startConfig,
						stepName: 'Start', // Start step name is always "Start"
					});
				}}
				initialConfig={
					// A start step's persisted config is a StartNodeConfig;
					// AutomationStep.config can't express that per-stepType
					// correlation (inputType is optional on StepConfig).
					step.config as StartNodeConfig
				}
			/>
		);
	}

	const body = (
		<div className="space-y-5 p-6">
			{/* Step Name */}
			<div>
				<label
					className={`text-sm font-medium mb-2 block ${colors.classes.textPrimary}`}
				>
					{t('Step Name')}
				</label>
				<Input
					value={stepName}
					onChange={(e) => {
						setStepName(e.target.value);
					}}
					placeholder={t('Enter step name')}
				/>
			</div>

			{/* Step-specific configuration */}
			{renderConfig()}
		</div>
	);

	// Inline mode (2.0 builder): the config expands under the step node — no
	// slide-over chrome, with the Cancel/Save buttons rendered inline.
	if (inline === true) {
		return (
			<div
				className={`rounded-lg border ${colors.classes.borderDefault} ${colors.classes.bgPrimary}`}
			>
				{body}
				<div className="flex items-center justify-between px-6 pb-6">
					<span className={`text-xs ${colors.classes.textSecondary}`}>
						{t('Changes are saved automatically')}
					</span>
					<div className="flex gap-2">
						<Button
							variant="secondary"
							onClick={handleRevert}
							disabled={!isDirty}
						>
							{t('Revert changes')}
						</Button>
						<Button variant="primary" onClick={onCancel}>
							{t('Done')}
						</Button>
					</div>
				</div>
			</div>
		);
	}

	return (
		<Sidebar
			isOpen={visible}
			onClose={onCancel}
			title={`${t('Configure')} ${step.stepName}`}
			width="lg"
			position="right"
			actions={[
				{
					label: t('Cancel'),
					onClick: onCancel,
					variant: 'secondary',
				},
				{
					label: t('Save'),
					onClick: handleSave,
					variant: 'primary',
				},
			]}
		>
			{body}
		</Sidebar>
	);
};

export default StepConfigSidebar;
